<script setup lang="ts">
//#region imports
import { computed, h, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { clone, omit } from 'lodash-es';
import { useApi } from '@/api';
import { useTableFilter, useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useCrmStatus } from '@/views/CRM/composables/useCrmStatus';
import CrmTag from '@/views/CRM/components/CrmTag.vue';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useLodash } from '@/setup/lodash';
import { usePermissions } from '@/composables/usePermissions';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetCrmActivitiesData, GetCrmActivitiesResponse, GetCrmSegmentsResponses, PostCrmActivitiesByIdCloneResponse } from '@/types/openapi-ts';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
import type { CrmActivityListItem } from '@/types/crm';
import type { SettingsPageQuery } from '@/types/core';
//#endregion

//#region types
interface Props {
  currentBrand: string;
}

interface CombinedFilterMetaItem {
  value: string;
  label: string;
  filter: string[];
}
type CrmActionType = 'email' | 'sms' | 'call';

type CrmActivitiesQuery = SettingsPageQuery<GetCrmActivitiesData['query']>;
//#endregion

//#region define
const props = defineProps<Props>();
//#endregion

//#region const
const LS_PATH = 'CRM/Activities';

const NOTIFY_PRESETS: Record<CrmActionType, 'notify-email' | 'notify-sms' | 'notify-call'> = {
  email: 'notify-email',
  sms: 'notify-sms',
  call: 'notify-call',
};

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<GetCrmActivitiesResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'moderation_status',
    label: 'computed_status',
    sortable: 'custom',
    type: 'status',
    width: '150',
    statusText: (value, row) => t(`crm.fields.computed_status.values.${detectCrmStatus(row)}`),
    status: (value, row) => detectCrmStatus(row),
  },
  {
    name: 'name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'crm_segment_name',
    label: 'segment',
    minWidth: '100',
  },
  {
    name: 'players_count',
    label: 'segment_players_count',
    sortable: 'custom',
    minWidth: '60',
    computedValue: (row) => (row.is_players_attached
      ? `${parseFloat(String(row.players_count || 0)).toLocaleString('en-US')}`
      : t('crm.status.updating')),
  },
  {
    name: 'action_types',
    label: 'actions',
    minWidth: '100',
    linkAppend: renderActions,
  },
  {
    name: 'activity_period_start',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
  {
    name: 'activity_period_end',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const router = useRouter();
const store = useTypedStore();
const updateUrl = useUpdateUrl();
const popupManager = usePopupManager();
const _ = useLodash();
const { hasPermission } = usePermissions();
const { t } = useI18n();
const { searchQuery } = useTableSearch();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});

const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
  initialOptions: {
    filter: {
      status: '',
      only_mine: false,
    },
  },
});

const {
  getStatusGroupFromList,
  sanitizeStatus,
  detectCrmStatus,
  isCanBeArchived,
  isCanBeCloned,
} = useCrmStatus();
//#endregion

//#region state
const tableData = ref<GetCrmActivitiesResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const permissions = ref<NonNullable<GetCrmActivitiesResponse['misc']>['permissions']>({});
//#endregion

//#region computed
const isSuperUser = computed(() => store.getters['auth/adminAcl']?.is_superuser);
const view = computed(() => route.meta?.view as string);

const viewsMeta = computed(() => [
  { name: 'activities', staticFilter: null, title: t('crm.activities.title'), hasCreateButton: true },
  {
    name: 'running',
    staticFilter: { status: ['active'], moderation_status: ['approved'], launch_status: ['running'] },
    title: t('crm.activities.title_running'),
    hasCreateButton: false,
    from: 'running',
  },
  {
    name: 'quality-assurance-activities',
    staticFilter: { status: ['active'], moderation_status: ['on_review'] },
    title: t('crm.activities.title_quality_assurance'),
    hasCreateButton: false,
    from: 'quality_assurance',
  },
]);

const combinedFilterMeta = computed<CombinedFilterMetaItem[]>(() => [
  { value: 'draft', label: t('crm.fields.computed_status.values.draft'), filter: ['draft'] },
  { value: 'on_review', label: t('crm.fields.computed_status.values.on_review'), filter: ['active', 'on_review'] },
  { value: 'for_rework', label: t('crm.fields.computed_status.values.for_rework'), filter: ['active', 'for_rework'] },
  { value: 'declined', label: t('crm.fields.computed_status.values.declined'), filter: ['active', 'declined'] },
  { value: 'approved', label: t('crm.fields.computed_status.values.approved'), filter: ['active', 'approved', 'pending'] },
  { value: 'running', label: t('crm.fields.computed_status.values.running'), filter: ['active', 'approved', 'running'] },
  { value: 'paused', label: t('crm.fields.computed_status.values.paused'), filter: ['active', 'approved', 'paused'] },
  { value: 'completed', label: t('crm.fields.computed_status.values.completed'), filter: ['active', 'approved', 'completed'] },
  { value: 'archived', label: t('crm.fields.computed_status.values.archived'), filter: ['archived'] },
]);

const viewsMetaIndex = computed<Record<string, any>>(() => {
  const result: Record<string, any> = {};
  viewsMeta.value.forEach((item) => {
    result[item.name] = item;
  });
  return result;
});

const currentViewMeta = computed(() => viewsMetaIndex.value[view.value] || viewsMetaIndex.value.activities);
const combinedFilterMetaIndex = computed<Record<string, CombinedFilterMetaItem>>(() => {
  const result: Record<string, CombinedFilterMetaItem> = {};
  combinedFilterMeta.value.forEach((item) => {
    result[item.value] = item;
  });
  return result;
});
const actionTypesIndex = computed<Record<string, CrmActionType[]>>(() => {
  const result: Record<string, CrmActionType[]> = {};
  tableData.value.forEach((activity) => {
    const types: CrmActionType[] = [];
    if (Array.isArray(activity.action_groups)) {
      activity.action_groups.forEach((actionGroup) => {
        if (Array.isArray(actionGroup.actions)) {
          actionGroup.actions.forEach((action) => {
            if (!types.includes(action.type)) {
              types.push(action.type);
            }
          });
        }
      });
    }
    result[activity.id] = types;
  });
  return result;
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    setFilter(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    Object.assign(optionsSort.value, _.pick(_urlData, Object.keys(optionsSort.value)));
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
    site_id: props.currentBrand,
  } as GetCrmActivitiesData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: CrmActivitiesQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  const currentStatus = combinedFilterMetaIndex.value[filterParams.value.status as string]?.filter || ['draft', 'active'];
  const dynamicFilters = currentViewMeta.value.staticFilter || getStatusGroupFromList(sanitizeStatus(currentStatus));

  options = {
    ...options,
    ...dynamicFilters,
  };
  if(filterParams.value.only_mine === true) {options.only_mine = filterParams.value.only_mine;}

  try {
    const { data: { payload, misc } } = await api.getActivities(options);

    count.value = misc!.count;
    permissions.value = misc?.permissions || {};
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function canEdit(id: string) {
  return isSuperUser.value || !!permissions.value[id]?.can_edit;
}

function renderActions(row: CrmActivityListItem) {
  const finalActions = (actionTypesIndex.value[row.id] || []).map((action) =>
    h(CrmTag, { preset: NOTIFY_PRESETS[action] }));
  return h('div', { class: 't:flex t:gap-4' }, finalActions);
}

function getActivityLink(row: CrmActivityListItem) {
  const params = currentViewMeta.value.from ? `?from=${currentViewMeta.value.from}` : '';
  return `/crm/activity/${row.id}${params}`;
}

function handleClone(row: CrmActivityListItem) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('crm.confirm.clone_activity.title'),
      message: t('crm.confirm.clone_activity.msg', { activityName: row.name }),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('crm.confirm.clone_activity.action'),
      confirmType: 'positive',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await cloneActivity(row.id);
        } finally {
          context.handleClose();
        }
      },
    },
  });
}

async function cloneActivity(id: string) {
  if (isDataLoading.value) return;
  isDataLoading.value = true;
  try {
    const { data: response } = await api.postActivityStatus(id, 'clone', {}) as unknown as { data: PostCrmActivitiesByIdCloneResponse };
    router.push({ name: 'crm.activity', params: { id: response.payload.id, isEdit: 'true' } });
  } finally {
    isDataLoading.value = false;
  }
}

function handleDelete(row: CrmActivityListItem) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('crm.confirm.delete_activity.title'),
      message: t('crm.confirm.delete_activity.msg', { activityName: row.name }),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('crm.confirm.delete_activity.action'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.postActivityStatus(row.id, 'archive', {});
          await fetchData();
        } finally {
          context.handleClose();
        }
      },
    },
  });
}

function handleAddActivity() {
  router.push('/crm/activity');
}
//#endregion

//#region watch
watch(() => [props.currentBrand, route.fullPath], async () => {
  await fetchData();
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="activities">
    <UiToolbar>
      <template v-slot:title>
        {{ currentViewMeta.title }}
      </template>
      <template v-slot:left-slot>
        <div class="t:flex t:space-x-8 t:items-center">
          <UiInput
            v-model.trim="searchQuery"
            :width="180"
            is-search
            @search="handleRefresh"
          />
          <UiSelectElement
            v-if="!currentViewMeta.staticFilter"
            v-model="filterParams.status"
            :placeholder="$t('crm.fields.computed_status.values.all')"
            :list="combinedFilterMeta"
            option-value-param="value"
            option-name-param="label"
            option-label-param="label"
            width="170"
            clearable
            @change="fetchData()"
          />
          <UiCheckbox
            v-model="filterParams.only_mine"
            :label="$t('crm.only_mine')"
            @change="fetchData()"
          />
        </div>
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="hasPermission('crm_activities_edit') && currentViewMeta.hasCreateButton"
          filled
          icon="plus"
          @click="handleAddActivity"
        >
          {{ $t('crm.activities.add_activity') }}
        </UiButton>
        <UiButton
          icon="sync-alt"
          :disabled="isDataLoading"
          @click="fetchData()"
        >
          {{ $t('reports.other.refresh') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="crm"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="100"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              :tooltip="$t('crm.buttons.view')"
              :router-to="getActivityLink(scope.row)"
            />
            <ActionIcon
              icon="clone"
              :tooltip="$t('crm.tooltips.clone')"
              :disabled="!canEdit(scope.row.id) || !isCanBeCloned(scope.row)"
              @click="handleClone(scope.row)"
            />
            <ActionIcon
              icon="trash-alt"
              :tooltip="$t('crm.buttons.archive')"
              color="var(--text-negative)"
              :disabled="!canEdit(scope.row.id) || !isCanBeArchived(scope.row)"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
